/**
 * BuilderSlideItem
 *
 * A single slide card inside the SlideBuilder panel.
 * Sortable via @dnd-kit/sortable — the GripVertical icon on the left is the
 * drag handle while the X button on the right removes the slide.
 *
 * Layout: vertical — full-width thumbnail on top, text details below.
 */

import type React from "react"
import { memo, useEffect, useRef, useState } from "react"

import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { useTranslation } from "react-i18next"

import type { BuilderSlide } from "../types"

import { GripVertical, X } from "@/components/icons"
import { cn } from "@/lib/utils"

import { SlideCategoryBadge } from "./SlideCategoryBadge"
import { SlideThumbnailImage } from "./SlideThumbnailImage"

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface BuilderSlideItemProps {
  builderSlide: BuilderSlide
  onRemove: (id: string) => void
  /** 1-based slide number in the builder. */
  index: number
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function BuilderSlideItemInner({ builderSlide, onRemove, index }: BuilderSlideItemProps) {
  const { t } = useTranslation()
  const { slide } = builderSlide
  const [isRemoving, setIsRemoving] = useState(false)
  const removeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (removeTimerRef.current !== null) {
        clearTimeout(removeTimerRef.current)
        removeTimerRef.current = null
      }
    }
  }, [])

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: builderSlide.id,
  })

  const style: React.CSSProperties = {
    transform: isRemoving ? undefined : CSS.Transform.toString(transform),
    transition: isRemoving
      ? "opacity 200ms ease, translate 200ms ease, scale 200ms ease"
      : (transition ?? "transform 200ms ease"),
    zIndex: isDragging ? 50 : undefined,
  }

  const displayTitle = slide.ai_title || `${t("slideSense.slides")} ${slide.slide_index + 1}`

  const handleRemove = () => {
    setIsRemoving(true)
    removeTimerRef.current = setTimeout(() => {
      removeTimerRef.current = null
      onRemove(builderSlide.id)
    }, 200)
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      aria-label={t("slideSense.dragHandle")}
      className={cn(
        "group bg-card hover:border-primary/30 focus-visible:ring-ring relative w-full min-w-0 cursor-grab touch-none overflow-hidden rounded-lg border shadow-sm transition-[border-color,box-shadow,opacity,translate,scale] duration-200 focus-visible:ring-2 focus-visible:outline-none active:cursor-grabbing",
        isDragging && "ring-primary/20 scale-[0.97] cursor-grabbing opacity-40 shadow-xl ring-2",
        isRemoving && "translate-x-4 scale-95 opacity-0"
      )}
    >
      {/* Drag-handle visual cue — always visible so users know the card is draggable */}
      <div
        aria-hidden="true"
        className="text-muted-foreground/60 group-hover:text-muted-foreground absolute top-2 left-1 z-10 rounded p-0.5"
      >
        <GripVertical className="h-4 w-4" />
      </div>

      {/* Remove button — right side. stopPropagation on pointerdown so the
          PointerSensor doesn't claim the gesture as a drag-start. */}
      <button
        aria-label={t("slideSense.removeFromBuilder")}
        className="text-muted-foreground hover:bg-destructive/10 hover:text-destructive absolute top-1.5 right-1.5 z-10 shrink-0 cursor-pointer rounded p-1 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
        type="button"
        onClick={handleRemove}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <X className="h-4 w-4" />
      </button>

      {/* Thumbnail image — full width, 16:9 */}
      <div className="bg-muted/40 relative aspect-[16/9] w-full overflow-hidden">
        {/* Slide number badge — top-left */}
        <div className="absolute top-1.5 left-7 z-10 flex h-5 min-w-5 items-center justify-center rounded bg-black/60 px-1 text-[10px] font-semibold text-white tabular-nums">
          {index}
        </div>
        <SlideThumbnailImage
          highRes
          alt={displayTitle}
          className="h-full w-full object-contain"
          slideId={slide.id}
          thumbnailUrl={slide.thumbnail_url}
        />
      </div>

      {/* Text details — below thumbnail */}
      <div className="space-y-1 p-2">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-sm leading-tight font-medium">{displayTitle}</span>
          <SlideCategoryBadge category={slide.category} />
        </div>
        <p className="text-muted-foreground truncate text-[11px]">{slide.source_filename}</p>
      </div>
    </div>
  )
}

// Memoized so hovering / dragging one builder item does not re-render its
// siblings — the prior all-items-re-render was the source of the flicker
// users saw while moving slides over the drop gaps. (#2)
export const BuilderSlideItem = memo(BuilderSlideItemInner)
